import './timeregistration-user-registrations-modal-entry-item.css';

import TIME_LOG_ENTRY_TYPES from '../../../constants/time/timeLogEntryTypes';
import { useState, useCallback, useContext } from 'react';
import InputField from '../../input-field/input-field';
import { Timestamp } from 'firebase/firestore';
import Button from '../../button/button';
import { useLogs } from '../../../logsContext';
import { UserInfoContext, EmployeesContext } from '../../../context';
import { currentTimeLogSetting } from '../../../utils/currentTimeLogSetting';
import DANISH_HOLIDAYS from '../../../constants/time/danishHolidays';

function EntryItemAdd({
  modalEmployeeId,
  modalDate,
  timeOffTypes,
  setAddMode,
}) {
  const [multipleDays, setMultipleDays] = useState(false);
  const [includeBreak, setIncludeBreak] = useState(false);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(modalDate),
    endDate: new Date(modalDate),
  });
  const [formValues, setFormValues] = useState({
    hours: 0,
    type: 'overtime',
    note: '',
    startTime: Timestamp.fromDate(
      (() => {
        const baseDate = new Date(modalDate);
        const now = new Date();
        baseDate.setHours(
          now.getHours(),
          Math.ceil(now.getMinutes() / 30) * 30,
          0,
          0
        );
        return baseDate;
      })()
    ),
    timeOffType: '',
    endTime: Timestamp.fromDate(
      (() => {
        const baseDate = new Date(modalDate);
        const now = new Date();
        baseDate.setHours(now.getHours() + 1, 0, 0, 0);
        return baseDate;
      })()
    ),
    breakWasIncluded: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { addLogEntry } = useLogs();
  const userInfo = useContext(UserInfoContext);
  const employees = useContext(EmployeesContext);

  // Calculate normTime for the selected employee based on date range
  const calculateNormTime = useCallback(
    (startDate, endDate) => {
      if (!userInfo?.companyData) return 0;

      const targetUserInfo = modalEmployeeId
        ? employees.find((emp) => emp.id === modalEmployeeId)?.data
        : userInfo;
      if (!targetUserInfo) return 0;

      let totalNormTime = 0;

      // Normalize dates to avoid timezone issues
      const normalizedStartDate = new Date(startDate);
      normalizedStartDate.setHours(12, 0, 0, 0); // Use noon to avoid DST issues

      const normalizedEndDate = new Date(endDate);
      normalizedEndDate.setHours(12, 0, 0, 0); // Use noon to avoid DST issues

      const currentDate = new Date(normalizedStartDate);
      const finalDate = new Date(normalizedEndDate);

      // Ensure we include both start and end dates
      while (currentDate <= finalDate) {
        const timeLogSetting = currentTimeLogSetting(
          userInfo.companyData,
          targetUserInfo,
          currentDate
        );

        if (timeLogSetting && !timeLogSetting.notWorking) {
          // Check if the day is a Danish holiday
          const isHoliday = DANISH_HOLIDAYS.some(
            (holiday) =>
              new Date(holiday.value).getTime() === currentDate.getTime()
          );

          if (!isHoliday) {
            // Calculate norm hours for this day
            const workingHours =
              (timeLogSetting.end.toMillis() - timeLogSetting.from.toMillis()) /
              (1000 * 60 * 60);
            let normTime = workingHours;

            // Handle break duration
            if (timeLogSetting.breakDuration > 0) {
              let breakDuration = timeLogSetting.breakDuration / 60;
              if (timeLogSetting.paidBreak) {
                breakDuration = 0;
              }
              normTime -= breakDuration;
            }

            totalNormTime += normTime;
          }
        }

        // Move to next day
        currentDate.setDate(currentDate.getDate() + 1);
      }

      return totalNormTime;
    },
    [userInfo, employees, modalEmployeeId]
  );

  // Calculate normTime for display
  const displayNormTime = multipleDays
    ? calculateNormTime(dateRange.startDate, dateRange.endDate)
    : 0;

  const handleChange = (e) => {
    const { name, value, checked } = e.target;

    // Handle the toggle switch for "multiple days"
    if (name === 'multipleDays') {
      setMultipleDays(checked);
      if (checked) {
        // When enabling "multiple days", update hours to show normTime
        const normTime = calculateNormTime(
          dateRange.startDate,
          dateRange.endDate
        );
        setFormValues({
          ...formValues,
          hours: normTime,
        });
      } else {
        // When disabling "multiple days", reset to half hour default
        setFormValues({
          ...formValues,
          hours: 0,
        });
      }
      return;
    }

    // Handle date range changes
    if (name === 'startDate' || name === 'endDate') {
      // The DatePicker component provides a Date object directly
      let inputDate;
      if (value instanceof Date) {
        // It's already a Date object from DatePicker
        inputDate = new Date(value);
      } else if (typeof value === 'string') {
        // Handle string input (fallback)
        inputDate = new Date(value);
      } else {
        console.error('Unexpected value type for date:', typeof value, value);
        return;
      }

      // Normalize to avoid timezone issues - use noon to avoid DST problems
      inputDate.setHours(12, 0, 0, 0);

      // Validate the date
      if (isNaN(inputDate.getTime())) {
        console.error('Invalid date created:', { name, value, inputDate });
        return; // Don't update state with invalid date
      }

      const newDateRange = {
        ...dateRange,
        [name]: inputDate,
      };
      setDateRange(newDateRange);

      // If "multiple days" is enabled, recalculate normTime
      if (multipleDays) {
        const normTime = calculateNormTime(
          newDateRange.startDate,
          newDateRange.endDate
        );
        setFormValues({
          ...formValues,
          hours: normTime,
        });
      }
      return;
    }

    // Handle time inputs (only for single day mode)
    if (name === 'endTime' || name === 'startTime') {
      // Use modalDate as the base date instead of preserving the existing date
      const date = new Date(modalDate);

      // Set the hours and minutes from 'value'
      const newTime = new Date(value);
      date.setHours(newTime.getHours());
      date.setMinutes(newTime.getMinutes());

      // Move to nearest 15 minutes, e.g. 12:07 -> 12:15 or 12:23 -> 12:30
      const minutesRounded = Math.ceil(date.getMinutes() / 15) * 15;
      date.setMinutes(minutesRounded);

      // Calculate hours
      if (name === 'endTime' && formValues.startTime) {
        const start = new Date(formValues.startTime.seconds * 1000).getTime();
        const end = date.getTime();
        let calculatedHours = (end - start) / 36e5;

        // Apply break adjustment if break is included
        if (includeBreak) {
          const targetUserInfo = modalEmployeeId
            ? employees.find((emp) => emp.id === modalEmployeeId)?.data
            : userInfo;

          if (targetUserInfo && userInfo?.companyData) {
            const timeLogSetting = currentTimeLogSetting(
              userInfo.companyData,
              targetUserInfo,
              new Date(formValues.startTime.seconds * 1000)
            );

            if (
              timeLogSetting?.breakDuration > 0 &&
              !timeLogSetting?.paidBreak
            ) {
              calculatedHours -= timeLogSetting.breakDuration / 60;
            }
          }
        }

        setFormValues({
          ...formValues,
          [name]: Timestamp.fromDate(date),
          hours: calculatedHours,
          breakWasIncluded: includeBreak,
        });
      } else if (name === 'startTime' && formValues.endTime) {
        const start = date.getTime();
        const end = new Date(formValues.endTime.seconds * 1000).getTime();
        let calculatedHours = (end - start) / 36e5;

        // Apply break adjustment if break is included
        if (includeBreak) {
          const targetUserInfo = modalEmployeeId
            ? employees.find((emp) => emp.id === modalEmployeeId)?.data
            : userInfo;

          if (targetUserInfo && userInfo?.companyData) {
            const timeLogSetting = currentTimeLogSetting(
              userInfo.companyData,
              targetUserInfo,
              date
            );

            if (
              timeLogSetting?.breakDuration > 0 &&
              !timeLogSetting?.paidBreak
            ) {
              calculatedHours -= timeLogSetting.breakDuration / 60;
            }
          }
        }

        setFormValues({
          ...formValues,
          [name]: Timestamp.fromDate(date),
          hours: calculatedHours,
          breakWasIncluded: includeBreak,
        });
      } else {
        setFormValues({
          ...formValues,
          [name]: Timestamp.fromDate(date),
          breakWasIncluded: includeBreak,
        });
      }
    } else {
      // Save as string
      if (name === 'type') {
        // When type changes, handle multipleDays and includeBreak resets
        const newFormValues = { ...formValues, [name]: value };

        // Reset multipleDays if new type doesn't support it
        if (
          value !== 'holiday' &&
          value !== 'timeOff' &&
          value !== 'sickLeave'
        ) {
          setMultipleDays(false);
          newFormValues.hours = 0.5; // Reset to default hours when switching away from multiple days types
        }

        // Reset includeBreak if switching to overtime and recalculate hours
        if (value === 'overtime' && includeBreak) {
          setIncludeBreak(false);

          // Recalculate hours without break adjustment
          const start = formValues.startTime.toMillis();
          const end = formValues.endTime.toMillis();
          const calculatedHours = (end - start) / 36e5; // Convert to hours without break adjustment

          newFormValues.hours = calculatedHours;
          newFormValues.breakWasIncluded = false;
        }

        setFormValues(newFormValues);
      } else {
        setFormValues({ ...formValues, [name]: value });
      }
    }
  };

  const handleIncludeBreakChange = (value) => {
    setIncludeBreak(value);

    // Only calculate break adjustment for single day mode
    if (!multipleDays) {
      const targetUserInfo = modalEmployeeId
        ? employees.find((emp) => emp.id === modalEmployeeId)?.data
        : userInfo;
      if (!targetUserInfo) return;

      // Get current time log setting
      const timeLogSetting = currentTimeLogSetting(
        userInfo.companyData,
        targetUserInfo,
        new Date(formValues.startTime.toDate())
      );

      // Calculate hours based on time difference
      const start = formValues.startTime.toMillis();
      const end = formValues.endTime.toMillis();
      let calculatedHours = (end - start) / 36e5; // Convert to hours

      // If break IS included (true), subtract break time from total hours
      // If break is NOT included (false), use the full time difference
      if (
        value &&
        timeLogSetting?.breakDuration > 0 &&
        !timeLogSetting?.paidBreak
      ) {
        calculatedHours -= timeLogSetting.breakDuration / 60; // Convert minutes to hours
      }

      setFormValues({
        ...formValues,
        hours: calculatedHours,
        breakWasIncluded: value,
      });
    }
  };

  const handleSubmit = useCallback(async () => {
    setIsSubmitting(true);

    try {
      if (multipleDays) {
        // Multiple days mode - create entries for each working day in the range
        // Normalize dates to avoid timezone issues
        const startDate = new Date(dateRange.startDate);
        startDate.setHours(12, 0, 0, 0); // Use noon to avoid DST issues

        const endDate = new Date(dateRange.endDate);
        endDate.setHours(12, 0, 0, 0); // Use noon to avoid DST issues

        const currentDate = new Date(startDate);
        const finalDate = new Date(endDate);

        const targetUserInfo = modalEmployeeId
          ? employees.find((emp) => emp.id === modalEmployeeId)?.data
          : userInfo;

        if (!targetUserInfo || !userInfo?.companyData) {
          throw new Error(
            'Unable to get user settings for multiple days registration'
          );
        }

        // Ensure we include both start and end dates
        while (currentDate <= finalDate) {
          const timeLogSetting = currentTimeLogSetting(
            userInfo.companyData,
            targetUserInfo,
            currentDate
          );

          if (timeLogSetting && !timeLogSetting.notWorking) {
            // Check if the day is a Danish holiday
            const isHoliday = DANISH_HOLIDAYS.some(
              (holiday) =>
                new Date(holiday.value).getTime() === currentDate.getTime()
            );

            if (!isHoliday) {
              // Calculate normTime for this specific day
              const workingHours =
                (timeLogSetting.end.toMillis() -
                  timeLogSetting.from.toMillis()) /
                (1000 * 60 * 60);
              let dayNormTime = workingHours;

              // Handle break duration
              if (timeLogSetting.breakDuration > 0) {
                let breakDuration = timeLogSetting.breakDuration / 60;
                if (timeLogSetting.paidBreak) {
                  breakDuration = 0;
                }
                dayNormTime -= breakDuration;
              }

              // Create proper timestamps for this specific date using the time settings
              const fromTime = timeLogSetting.from.toDate();
              const endTime = timeLogSetting.end.toDate();

              // Create new timestamps with the current registration date but the time from settings
              const registrationStartTime = new Date(currentDate);
              registrationStartTime.setHours(
                fromTime.getHours(),
                fromTime.getMinutes(),
                0,
                0
              );

              const registrationEndTime = new Date(currentDate);
              registrationEndTime.setHours(
                endTime.getHours(),
                endTime.getMinutes(),
                0,
                0
              );

              // Prepare the new entry data for this day
              const newEntryData = {
                hours: dayNormTime,
                type: formValues.type,
                note: formValues.note,
                date: Timestamp.fromDate(currentDate),
                startTime: Timestamp.fromDate(registrationStartTime),
                endTime: Timestamp.fromDate(registrationEndTime),
                breakWasIncluded: true, // For multiple days, breaks are always included in normTime calculation
                // Include timeOffType only if type is 'timeOff'
                ...(formValues.type === 'timeOff' && {
                  timeOffType: formValues.timeOffType,
                }),
              };

              // Call addLogEntry for this specific date
              await addLogEntry(modalEmployeeId, currentDate, newEntryData);
            }
          }
          currentDate.setDate(currentDate.getDate() + 1);
        }
      } else {
        // Single day mode - original logic
        const newEntryData = {
          hours: formValues.hours,
          type: formValues.type,
          note: formValues.note,
          date: Timestamp.fromDate(new Date(modalDate)),
          startTime: formValues.startTime, // Already a Timestamp
          endTime: formValues.endTime, // Already a Timestamp
          breakWasIncluded: includeBreak,
          // Include timeOffType only if type is 'timeOff'
          ...(formValues.type === 'timeOff' && {
            timeOffType: formValues.timeOffType,
          }),
        };

        // Call addLogEntry with userId, date, and newEntryData
        await addLogEntry(modalEmployeeId, modalDate, newEntryData);
      }

      // Reset the form to initial state
      setFormValues({
        hours: 0,
        type: 'overtime',
        note: '',
        startTime: Timestamp.fromDate(
          (() => {
            const baseDate = new Date(modalDate);
            const now = new Date();
            baseDate.setHours(
              now.getHours(),
              Math.ceil(now.getMinutes() / 30) * 30,
              0,
              0
            );
            return baseDate;
          })()
        ),
        timeOffType: '',
        endTime: Timestamp.fromDate(
          (() => {
            const baseDate = new Date(modalDate);
            const now = new Date();
            baseDate.setHours(now.getHours() + 1, 0, 0, 0);
            return baseDate;
          })()
        ),
        breakWasIncluded: false,
      });

      // Reset multiple days mode and include break
      setMultipleDays(false);
      setIncludeBreak(false);
      setDateRange({
        startDate: new Date(modalDate),
        endDate: new Date(modalDate),
      });

      // Exit add mode
      setAddMode(false);
    } catch (error) {
      console.error('Error adding new log entry:', error);
      alert('Failed to add log entry. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }, [
    multipleDays,
    modalDate,
    setAddMode,
    dateRange.startDate,
    dateRange.endDate,
    modalEmployeeId,
    employees,
    userInfo,
    formValues.type,
    formValues.note,
    formValues.timeOffType,
    formValues.hours,
    formValues.startTime,
    formValues.endTime,
    addLogEntry,
    includeBreak,
  ]);

  return (
    <div className="timeregistration-user-registrations-modal-entry-item">
      <div className="timeregistration-user-registrations-modal-entry-item-form-row">
        <InputField
          name="type"
          type="select"
          label="Type"
          placeholder="- Vælg -"
          options={TIME_LOG_ENTRY_TYPES.map((type) => ({
            value: type.value,
            label: type.label,
          }))}
          value={formValues.type}
          onChange={handleChange}
          className={`timeregistration-user-registrations-modal-entry-item-form__type ${
            formValues.type === 'timeOff'
              ? 'timeregistration-user-registrations-modal-entry-item-form__type--timeOff'
              : ''
          }`}
        />
        {formValues.type === 'timeOff' && (
          <InputField
            name="timeOffType"
            type="select"
            label="Årsag"
            placeholder="- Vælg -"
            options={timeOffTypes}
            value={formValues.timeOffType}
            onChange={handleChange}
            className={`timeregistration-user-registrations-modal-entry-item-form__type ${
              formValues.type === 'timeOff'
                ? 'timeregistration-user-registrations-modal-entry-item-form__type--timeOff'
                : ''
            }`}
          />
        )}
      </div>

      {/* Toggle for "multiple days" mode - only show for holiday and timeOff */}
      {(formValues.type === 'holiday' ||
        formValues.type === 'timeOff' ||
        formValues.type === 'sickLeave') && (
        <div className="timeregistration-user-registrations-modal-entry-item-form-row">
          <InputField
            name="multipleDays"
            type="switch"
            label="Vælg flere dage"
            showLabel
            checked={multipleDays}
            onChange={handleChange}
            className="timeregistration-user-registrations-modal-entry-item-form__switch"
          />
        </div>
      )}
      {/* Toggle for "include break" - only show in single day mode, not for overtime, and not for hourly paid employees */}
      {!multipleDays &&
        formValues.type !== 'overtime' &&
        (() => {
          // Check if the employee is hourly paid
          const targetUserInfo = modalEmployeeId
            ? employees.find((emp) => emp.id === modalEmployeeId)?.data
            : userInfo;

          if (!targetUserInfo || !userInfo?.companyData) {
            return false;
          }

          const timeLogSetting = currentTimeLogSetting(
            userInfo.companyData,
            targetUserInfo,
            new Date(formValues.startTime.toDate())
          );

          // Don't show break toggle if hourly paid (hourly paid employees don't have paid breaks)
          return !timeLogSetting?.isPaidHourly && !timeLogSetting?.paidBreak;
        })() && (
          <div className="timeregistration-user-registrations-modal-entry-item-form-row">
            <InputField
              name="includeBreak"
              type="switch"
              label="Medregn pause"
              showLabel
              checked={includeBreak}
              onChange={() => handleIncludeBreakChange(!includeBreak)}
              className="timeregistration-user-registrations-modal-entry-item-form__switch"
            />
          </div>
        )}

      {/* Date range inputs when "multiple days" is enabled */}
      {multipleDays ? (
        <div className="timeregistration-user-registrations-modal-entry-item-form-row">
          <InputField
            name="startDate"
            type="date"
            label="Fra"
            placeholder="Fra"
            datePickerPosition={'bottom'}
            value={dateRange.startDate}
            onChange={handleChange}
            className="timeregistration-user-registrations-modal-entry-item-form__date"
          />
          <InputField
            name="endDate"
            type="date"
            label="Til"
            placeholder="Til"
            datePickerPosition={'bottom'}
            value={dateRange.endDate}
            onChange={handleChange}
            className="timeregistration-user-registrations-modal-entry-item-form__date"
          />
          {multipleDays && (
            <InputField
              name="totalDays"
              label="Dage"
              type="number"
              key={`total-days-${displayNormTime}`}
              // Round up to nearest 1, 2, 3 etc.
              value={Math.ceil(formValues.hours / 7.5)}
              disabled
              className="timeregistration-user-registrations-modal-entry-item-form__days"
            />
          )}
        </div>
      ) : (
        <div className="timeregistration-user-registrations-modal-entry-item-form-row">
          <InputField
            name="startTime"
            type="time"
            label="Fra"
            placeholder="Fra"
            value={formValues.startTime.toDate()}
            onChange={handleChange}
            className="timeregistration-user-registrations-modal-entry-item-form__time"
          />
          <InputField
            name="endTime"
            type="time"
            label="Til"
            placeholder="Til"
            value={formValues.endTime.toDate()}
            onChange={handleChange}
            className="timeregistration-user-registrations-modal-entry-item-form__time"
          />
          <InputField
            name="hours"
            label="Timer"
            type="number"
            key={`hours-${formValues.hours}`}
            value={formValues.hours}
            disabled
            className="timeregistration-user-registrations-modal-entry-item-form__hours"
          />
        </div>
      )}

      <InputField
        name="note"
        label="Note"
        type="text"
        onChange={handleChange}
        value={formValues.note || ''}
      />
      <div className="timeregistration-user-registrations-modal-entry-item-form__buttons">
        <Button
          buttonStyle="text-link"
          className="small"
          onClick={() => setAddMode(false)}
        >
          Luk
        </Button>
        <Button onClick={handleSubmit} disabled={isSubmitting}>
          {isSubmitting ? 'Gemmer...' : 'Gem'}
        </Button>
      </div>
    </div>
  );
}

export default EntryItemAdd;
